import { GettingStartedCardFrame } from "@/components/getting-started-card";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconColor,
  MDSIconKind,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { serviceInfoApiClientManager } from "@/manager/backend-api-client";
import { electronModule } from "@/modules/electron";
import type {
  DesktopCriticalUpdatePromptSource,
  DesktopUpdateRestartBlocker,
  DesktopUpdateStatus,
} from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lt, valid } from "semver";

type DesktopCriticalMinimumVersions = {
  darwin: string | null;
  win32: string | null;
};

export type DesktopUpdateNoticePresentation = "expanded-sidebar" | "collapsed-sidebar";

interface DesktopUpdateNoticeProps {
  presentation?: DesktopUpdateNoticePresentation;
}

export const isCriticalDesktopVersion = ({
  currentVersion,
  minimumVersion,
}: {
  currentVersion: string | null | undefined;
  minimumVersion: string | null | undefined;
}): boolean => {
  if (!currentVersion || !minimumVersion) return false;
  if (!valid(currentVersion) || !valid(minimumVersion)) return false;

  return lt(currentVersion, minimumVersion);
};

const loadCriticalMinimumVersions = async (): Promise<
  DesktopCriticalMinimumVersions | null | undefined
> => {
  try {
    const client = await serviceInfoApiClientManager.initialize({});
    const { data } = await client.GET("/v1/service-info");
    return data?.desktop.critical_minimum_version ?? null;
  } catch (error) {
    logger.warn({
      message: "[desktop-update] failed to load critical minimum versions",
      info: { error: error instanceof Error ? error.message : String(error) },
    });
    return undefined;
  }
};

export const DesktopUpdateNotice = observer(function DesktopUpdateNotice({
  presentation = "expanded-sidebar",
}: DesktopUpdateNoticeProps) {
  const { store } = useAppStore();
  const [status, setStatus] = useState<DesktopUpdateStatus | null>(null);
  const [isRestartDialogOpen, setIsRestartDialogOpen] = useState(false);
  const [criticalMinimumVersions, setCriticalMinimumVersions] =
    useState<DesktopCriticalMinimumVersions | null>(null);
  const lastAutomaticallyPromptedVersion = useRef<string | null>(null);
  const lastHandledCheckSequence = useRef(0);
  const electronAppInfo = store.electron?.electronAppInfo;
  const isCollapsedSidebar = presentation === "collapsed-sidebar";

  useEffect(() => {
    if (!store.interface.isDesktop) return;

    let isCancelled = false;

    const refreshCriticalMinimumVersions = async () => {
      const versions = await loadCriticalMinimumVersions();

      if (!isCancelled && versions !== undefined) setCriticalMinimumVersions(versions);
    };

    void electronModule.desktopUpdater.getStatus().then(initialStatus => {
      if (!isCancelled) setStatus(initialStatus);
    });
    const unsubscribe = electronModule.desktopUpdater.onStatusChanged({
      callback: nextStatus => {
        setStatus(nextStatus);

        if (nextStatus.phase === "checking") {
          void refreshCriticalMinimumVersions();
        }
      },
    });
    void refreshCriticalMinimumVersions();

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, [store.interface.isDesktop]);

  useEffect(() => {
    if (!status || status.lastCheckSequence <= lastHandledCheckSequence.current) return;

    lastHandledCheckSequence.current = status.lastCheckSequence;

    if (status.lastCheckResult === "up-to-date") {
      toastModule.triggerToast({ content: "Mem is up to date." });
    } else if (status.lastCheckResult === "error") {
      toastModule.triggerToast({ content: "We couldn’t check for updates. Try again." });
    }
  }, [status]);

  const minimumVersion = useMemo(() => {
    const platform = electronAppInfo?.electronPlatform;

    if (platform !== "darwin" && platform !== "win32") return null;

    return criticalMinimumVersions?.[platform] ?? null;
  }, [criticalMinimumVersions, electronAppInfo?.electronPlatform]);

  const isCritical = isCriticalDesktopVersion({
    currentVersion: electronAppInfo?.electronAppVersion,
    minimumVersion,
  });
  const regularUpdateIsReady = status?.phase === "ready" && !isCritical;
  const rendererRecordingIsActive =
    Boolean(store.audio?.hasActiveRecording) || Boolean(store.audio?.recording?.isInitializing);
  const restartIsBlockedByRecording =
    rendererRecordingIsActive || Boolean(status?.restartBlockedByRecording);
  const restartBlockers: DesktopUpdateRestartBlocker[] = [];

  if (restartIsBlockedByRecording) restartBlockers.push("recording");

  useEffect(() => {
    if (!regularUpdateIsReady) setIsRestartDialogOpen(false);
  }, [regularUpdateIsReady]);

  const getCurrentRestartBlockers = useCallback(() => {
    const currentBlockers: DesktopUpdateRestartBlocker[] = [];
    const recordingIsActive =
      Boolean(store.audio?.hasActiveRecording) || Boolean(store.audio?.recording?.isInitializing);

    if (recordingIsActive) currentBlockers.push("recording");

    return currentBlockers;
  }, [store]);

  const requestCriticalUpdateInstall = useCallback(
    async (source: DesktopCriticalUpdatePromptSource) => {
      const promptResult = await electronModule.desktopUpdater.promptCriticalUpdate({ source });

      if (promptResult !== "restart_requested") return;

      const result = await electronModule.desktopUpdater.install({
        isCritical: true,
        restartBlockers: getCurrentRestartBlockers(),
      });

      if (result?.status === "blocked" && result.blocker === "recording") {
        setStatus(current => (current ? { ...current, restartBlockedByRecording: true } : current));
      }
    },
    [getCurrentRestartBlockers]
  );

  useEffect(() => {
    const targetVersion = status?.targetVersion;

    if (
      !isCritical ||
      status?.phase !== "ready" ||
      !targetVersion ||
      restartBlockers.length > 0 ||
      lastAutomaticallyPromptedVersion.current === targetVersion
    ) {
      return;
    }

    lastAutomaticallyPromptedVersion.current = targetVersion;
    void requestCriticalUpdateInstall("automatic");
  }, [isCritical, requestCriticalUpdateInstall, restartBlockers.length, status]);

  if (!store.interface.isDesktop || !status) return null;

  const manualCheckIsInProgress =
    status.isManualCheck && (status.phase === "checking" || status.phase === "downloading");

  if (!isCritical && !regularUpdateIsReady && !manualCheckIsInProgress) return null;

  const blockerMessage = restartIsBlockedByRecording
    ? "Finish recording before restarting."
    : null;
  const actionLabel = (() => {
    if (status.phase === "ready") return "Relaunch to update";
    if (status.phase === "checking") return "Checking for updates";
    if (status.phase === "downloading") return "Downloading update";
    return "Download update";
  })();
  const targetVersion = status.targetVersion ? `v${status.targetVersion.replace(/^v/, "")}` : null;
  const actionIsBusy = status.phase === "checking" || status.phase === "downloading";
  const actionIsDisabled = actionIsBusy || (status.phase === "ready" && restartBlockers.length > 0);

  const handlePrimaryAction = async () => {
    if (status.phase !== "ready") {
      const nextStatus = await electronModule.desktopUpdater.check();
      setStatus(nextStatus);
      return;
    }

    if (isCritical) {
      await requestCriticalUpdateInstall("manual");
      return;
    }

    const result = await electronModule.desktopUpdater.install({ isCritical, restartBlockers });

    if (result?.status === "blocked" && result.blocker === "recording") {
      setStatus(current => (current ? { ...current, restartBlockedByRecording: true } : current));
    }
  };

  const handleCollapsedPrimaryAction = async () => {
    if (regularUpdateIsReady) {
      setIsRestartDialogOpen(true);
      return;
    }

    await handlePrimaryAction();
  };

  const handleRestartNow = async () => {
    setIsRestartDialogOpen(false);
    await handlePrimaryAction();
  };

  if (isCollapsedSidebar) {
    const iconKind = actionIsBusy
      ? MDSIconKind.Spinner
      : isCritical
        ? MDSIconKind.Warning
        : MDSIconKind.ArrowDownToLine;

    return (
      <>
        <CompactNotice aria-label="Desktop update" aria-live="polite" role="region">
          <MDSTooltip
            config={{
              label: blockerMessage ?? actionLabel,
              placement: MDSTooltipPlacement.Right,
            }}
          >
            <CompactUpdateButton
              $isCritical={isCritical}
              aria-label={actionLabel}
              iconKind={iconKind}
              isDisabled={actionIsDisabled}
              onClick={handleCollapsedPrimaryAction}
              size={MDSIconButtonSize.Small}
              variant={MDSIconButtonVariant.OnChrome}
            />
          </MDSTooltip>
        </CompactNotice>
        <MDSModalDialog
          handleClose={() => setIsRestartDialogOpen(false)}
          isOpen={isRestartDialogOpen}
        >
          <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
            Restart Mem to update?
          </MDSText>
          <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
            <MDSText variant={MDSTextVariant.ParagraphS}>
              An update is ready to install. Restart Mem now, or install it the next time you
              restart.
            </MDSText>
            {blockerMessage && (
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
                {blockerMessage}
              </MDSText>
            )}
          </MDSDialogTextContent>
          <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
            <MDSButton
              onClick={() => setIsRestartDialogOpen(false)}
              variant={MDSButtonVariant.Transparent}
            >
              Next restart
            </MDSButton>
            <MDSButton
              isDisabled={actionIsDisabled}
              onClick={handleRestartNow}
              variant={MDSButtonVariant.Contrast}
            >
              Restart now
            </MDSButton>
          </MDSButtonGroup>
        </MDSModalDialog>
      </>
    );
  }

  return (
    <UpdateCardFrame
      aria-label="Desktop update"
      aria-live="polite"
      canCollapse={false}
      role="region"
      title={
        <PrimaryAction
          aria-label={actionLabel}
          isDisabled={actionIsDisabled}
          onClick={handlePrimaryAction}
          size={MDSButtonSize.Small}
          variant={isCritical ? MDSButtonVariant.Danger : MDSButtonVariant.Transparent}
        >
          <ActionLead>
            <MDSIcon
              color={isCritical ? MDSIconColor.Danger : MDSIconColor.Emphasis}
              kind={
                actionIsBusy
                  ? MDSIconKind.Spinner
                  : isCritical
                    ? MDSIconKind.Warning
                    : MDSIconKind.ArrowDownToLine
              }
              size={12}
            />
            <ActionCopy>
              <ActionTitle>{actionLabel}</ActionTitle>
              {targetVersion && (
                <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
                  {targetVersion}
                </MDSText>
              )}
            </ActionCopy>
          </ActionLead>
          {!actionIsBusy && (
            <ActionChevron kind={MDSIconKind.AngleRight} slot={MDSButtonSlotName.RightIcon} />
          )}
        </PrimaryAction>
      }
    >
      {blockerMessage && status.phase === "ready" && (
        <BlockerMessage color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
          {blockerMessage}
        </BlockerMessage>
      )}
    </UpdateCardFrame>
  );
});

const CompactNotice = styled.div({
  display: "flex",
});

const CompactUpdateButton = styled(MDSIconButton)<{ $isCritical: boolean }>(
  ({ $isCritical, theme }) => ({
    ".mds-icon": {
      color: $isCritical ? theme.colors.icon.danger : theme.colors.icon.emphasis,
      fill: $isCritical ? theme.colors.icon.danger : theme.colors.icon.emphasis,
    },
  })
);

const UpdateCardFrame = styled(GettingStartedCardFrame)`
  && {
    flex-shrink: 0;
    gap: 0;
    overflow: hidden;
    padding: 0;
  }
`;

const ActionLead = styled.span(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing["2"],
  minWidth: 0,
  transform: "translateY(-4px)",
}));

const ActionCopy = styled.span({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const ActionChevron = styled(MDSIcon)({
  transform: "translateY(-4px)",
});

const ActionTitle = styled.span({
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const BlockerMessage = styled(MDSText)(({ theme }) => ({
  padding: `0 ${theme.spacing["3"]} ${theme.spacing["2"]}`,
  width: "100%",
}));

const PrimaryAction = styled(MDSButton)(({ theme }) => ({
  "&&": {
    borderRadius: theme.borderRadius.large,
    height: "auto",
    justifyContent: "space-between",
    minHeight: "32px",
    padding: theme.spacing["3"],
    textAlign: "left",
    width: "100%",
  },
  "&&[data-hovered]:not([data-disabled])": {
    backgroundColor: theme.colors.background.hoverOverlay.chrome,
  },
}));
